import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  selectBookingPageSettings,
  selectBookingPageSettingsError,
  selectBookingPageSettingsStatus,
} from "../../features/trial-requests/trial-requests-slice";
import { updateBookingPageSettings } from "../../features/trial-requests/slice-extensions/trial-requests-slice-extension";
import { toUpdateRequest } from "../../features/trial-requests/utils/bookingPage";

// Max 63 characters: the slug doubles as a *.timeplanned.com DNS label.
const SLUG_PATTERN = /^[a-z0-9][a-z0-9-]{0,61}[a-z0-9]$/;

const emptyToNull = (value: string): string | null => (value.trim() === "" ? null : value.trim());

/**
 * Personalizes the public booking page: its address (slug), the shown name
 * (defaults to the instructor's own name) and the intro texts (default to the
 * standard copy in the visitor's language).
 */
const BookingPagePersonalizationCard = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const settings = useAppSelector(selectBookingPageSettings);
  const status = useAppSelector(selectBookingPageSettingsStatus);
  const error = useAppSelector(selectBookingPageSettingsError);
  const [slug, setSlug] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [introText, setIntroText] = useState("");
  const [contactIntroText, setContactIntroText] = useState("");
  const [ctaText, setCtaText] = useState("");

  useEffect(() => {
    if (!settings) return;
    setSlug(settings.slug ?? "");
    setDisplayName(settings.displayName ?? "");
    setIntroText(settings.introText ?? "");
    setContactIntroText(settings.contactIntroText ?? "");
    setCtaText(settings.ctaText ?? "");
  }, [settings]);

  const dirty =
    !!settings &&
    (slug.trim() !== (settings.slug ?? "") ||
      emptyToNull(displayName) !== (settings.displayName ?? null) ||
      emptyToNull(introText) !== (settings.introText ?? null) ||
      emptyToNull(contactIntroText) !== (settings.contactIntroText ?? null) ||
      emptyToNull(ctaText) !== (settings.ctaText ?? null));

  const slugValid = SLUG_PATTERN.test(slug.trim());

  const handleSave = () => {
    if (!settings) return;
    dispatch(
      updateBookingPageSettings({
        ...toUpdateRequest(settings),
        slug: slug.trim(),
        displayName: emptyToNull(displayName),
        introText: emptyToNull(introText),
        contactIntroText: emptyToNull(contactIntroText),
        ctaText: emptyToNull(ctaText),
      }),
    );
  };

  if (!settings?.enabled) {
    return null;
  }

  return (
    <div className="with-border rounded-2xl bg-white p-4 dark:bg-primary-10 lg:p-6">
      <h2 className="text-lg font-semibold text-grey-20 dark:text-white">
        {t("trialRequests.personalizationTitle", "Personalization")}
      </h2>
      <p className="mt-1 text-sm text-grey-40 dark:text-grey-80">
        {t(
          "trialRequests.personalizationIntro",
          "Make the page yours: pick its address and name, and rewrite the intro texts. Empty fields fall back to your own name and the standard text in the visitor's language.",
        )}
      </p>

      <label className="mt-4 block text-sm font-medium text-grey-30 dark:text-grey-70">
        {t("trialRequests.slugLabel", "Page address")}
        <span className="mt-1 flex items-center gap-1">
          <span className="text-sm text-grey-40 dark:text-grey-80">https://</span>
          <input
            className="input min-w-0 flex-1"
            value={slug}
            onChange={(event) => setSlug(event.target.value.toLowerCase())}
          />
          <span className="text-sm text-grey-40 dark:text-grey-80">.timeplanned.com</span>
        </span>
        {!slugValid && slug.trim() !== "" && (
          <span className="field-error mt-1 block">
            {t(
              "trialRequests.slugInvalid",
              "2–63 characters: lowercase letters, digits and hyphens, starting and ending with a letter or digit.",
            )}
          </span>
        )}
      </label>

      <label className="mt-4 block text-sm font-medium text-grey-30 dark:text-grey-70">
        {t("trialRequests.displayNameLabel", "Display name")}
        <input
          className="input mt-1"
          value={displayName}
          maxLength={100}
          onChange={(event) => setDisplayName(event.target.value)}
          placeholder={t("trialRequests.displayNamePlaceholder", "Your own name by default")}
        />
      </label>

      <label className="mt-4 block text-sm font-medium text-grey-30 dark:text-grey-70">
        {t("trialRequests.ctaLabel", "Main button label")}
        <input
          className="input mt-1"
          value={ctaText}
          maxLength={60}
          onChange={(event) => setCtaText(event.target.value)}
          placeholder={t("trialRequests.ctaPlaceholder", "“Request a trial lesson” by default")}
        />
        <span className="mt-1 block text-xs font-normal text-grey-40 dark:text-grey-80">
          {t("trialRequests.ctaHint", "Not teaching lessons? Make it “Book a session”, “Request a concert” — anything that fits what you offer.")}
        </span>
      </label>

      <label className="mt-4 block text-sm font-medium text-grey-30 dark:text-grey-70">
        {t("trialRequests.introLabel", "Booking page intro")}
        <textarea
          className="input mt-1"
          rows={2}
          maxLength={500}
          value={introText}
          onChange={(event) => setIntroText(event.target.value)}
          placeholder={t("trialRequests.textPlaceholder", "Standard text by default")}
        />
      </label>

      <label className="mt-4 block text-sm font-medium text-grey-30 dark:text-grey-70">
        {t("trialRequests.contactIntroLabel", "Contact page intro")}
        <textarea
          className="input mt-1"
          rows={2}
          maxLength={500}
          value={contactIntroText}
          onChange={(event) => setContactIntroText(event.target.value)}
          placeholder={t("trialRequests.textPlaceholder", "Standard text by default")}
        />
      </label>

      {error && <p className="field-error mt-2">{error}</p>}

      {dirty && (
        <button
          type="button"
          className="btn btn-primary mt-4"
          onClick={handleSave}
          disabled={status === "loading" || !slugValid}
        >
          {t("trialRequests.savePersonalization", "Save changes")}
        </button>
      )}
    </div>
  );
};

export default BookingPagePersonalizationCard;
